'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useRef } from 'react';
type TrendProps = {
  currentValue: string | number | null | undefined;
  children: ReactNode;
  resetKey?: string;
};
const parseValue = (value: string | number | null | undefined): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const strValue = String(value).replace(',', '.');
  const parsed = Number(strValue);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Trend color wrapper component.
 * Tracks value changes and applies trend color to child element.
 * Uses internal ref to compare with previous value and display color based on direction.
 *
 * @param root0 props
 * @param root0.currentValue current value for trend detection
 * @param root0.children child element to colorize
 * @param root0.resetKey key that resets trend state when scope changes
 * @returns trend color wrapper component
 */
export function Trend(t0) {
  const $ = _c(8);
  const {
    currentValue,
    children,
    resetKey
  } = t0;
  const elementRef = useRef(null);
  const previousValueRef = useRef(undefined);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const element = elementRef.current;
      if (!element) {
        return;
      }
      previousValueRef.current = undefined;
      element.classList.remove("text-success", "text-important", "delay-none");
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== resetKey) {
    t2 = [resetKey];
    $[1] = resetKey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== currentValue) {
    t3 = () => {
      const element_0 = elementRef.current;
      if (!element_0) {
        return;
      }
      const current = parseValue(currentValue);
      const prev = previousValueRef.current;
      previousValueRef.current = current;
      if (prev === undefined || current === undefined || prev === current) {
        element_0.classList.remove("text-success", "text-important", "delay-none");
        return;
      }
      const handleTransitionEnd = () => {
        element_0.classList.remove("delay-none");
        element_0.classList.add("delay-5000");
        element_0.classList.remove("text-success", "text-important");
        element_0.removeEventListener("transitionend", handleTransitionEnd);
      };
      element_0.removeEventListener("transitionend", handleTransitionEnd);
      element_0.addEventListener("transitionend", handleTransitionEnd);
      element_0.classList.remove("delay-5000");
      element_0.classList.add("delay-none", current > prev ? "text-success" : "text-important");
      return () => {
        element_0.removeEventListener("transitionend", handleTransitionEnd);
      };
    };
    t4 = [currentValue];
    $[3] = currentValue;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== children) {
    t5 = <span ref={elementRef} className="inline-block transition-colors duration-1000 ease-in-out">{children}</span>;
    $[6] = children;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}